import useMobile from 'hooks/useMobile';
import { Product } from 'lib/product';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NewProductCard } from './NewProductCard';
import ProductViewDialog from './NewProductCard/Dialog/ProductViewDialog';
import { ProductGallerySkeleton } from './ProductGallery';
import { usePopupContext } from 'lib/popups';
import * as analytics from 'lib/analytics';

interface ProductsGridProps {
  isLoading: boolean;
  isLoadingMoreProducts: boolean;
  products: Product[];
  loadedCount: number;
  onLoadMore: () => void;
  showEmptyState?: boolean;
}

const ProductsGrid = ({
  isLoading,
  isLoadingMoreProducts,
  products = [],
  loadedCount,
  onLoadMore,
  showEmptyState = true,
}: ProductsGridProps) => {
  const isMobile = useMobile();
  const {
    trackProductView,
    triggerPostAddToCartPopup,
  } = usePopupContext();

  const [
    selectedProduct,
    setSelectedProduct,
  ] = useState<Product | null>(null);
  const [scrollPos, setScrollPos] = useState(0);
  const loaderRef = useRef<HTMLButtonElement>(null);

  const onCloseProductView = useCallback(
    (product: Product) => {
      if (modalPushedRef.current) {
        modalPushedRef.current = false;
        window.history.replaceState({}, '');
      }

      setSelectedProduct(null);
      if (product?.id) trackProductView(product.id);
    },
    [trackProductView],
  );

  const modalPushedRef = useRef(false);

  useEffect(() => {
    if (!isMobile || !selectedProduct) return;
    setScrollPos(window.scrollY);

    if (!modalPushedRef.current) {
      window.history.pushState({ modal: true }, '');
      modalPushedRef.current = true;
    }

    const handlePopState = () => {
      modalPushedRef.current = false;
      setSelectedProduct(null);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMobile, onCloseProductView, scrollPos, selectedProduct]);

  const visibleProducts = useMemo(
    () => products.slice(0, loadedCount),
    [products, loadedCount],
  );

  const hasMoreProducts = loadedCount < products.length;

  const onClickLoadMore = useCallback(() => {
    if (loadedCount < products.length) {
      onLoadMore();
    }
  }, [loadedCount, onLoadMore, products.length]);

  useEffect(() => {
    if (!hasMoreProducts || isLoadingMoreProducts) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        onLoadMore();
      }
    });

    const loader = loaderRef.current;
    if (loader) observer.observe(loader);

    return () => {
      if (loader) observer.unobserve(loader);
    };
  }, [hasMoreProducts, isLoadingMoreProducts, onLoadMore]);

  if (isLoading) {
    return (
      <ProductGallerySkeleton
        width={isMobile ? 560 : 1000}
        column={isMobile ? 2 : 3}
      />
    );
  }

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4 md:tw-gap-6">
          {showEmptyState && visibleProducts.length === 0 && (
            <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-col-span-2 md:tw-col-span-3">
              <h5 className="tw-mb-2 tw-text-center">
                Something Yummy is on the way.
              </h5>
              <p className="tw-mb-0 tw-flex tw-items-center tw-justify-center tw-text-center">
                While we’re prepping them, fuel up with our full menu
                of goal-friendly meals!
              </p>
            </div>
          )}
          {visibleProducts.map((product) => (
            <NewProductCard
              key={product.id}
              product={product}
              onShowQuickView={() => {
                setSelectedProduct(product);
                analytics.viewProduct({ product });
              }}
              onFirstItemAdded={triggerPostAddToCartPopup}
            />
          ))}
        </div>
        <div className="tw-pt-16 tw-pb-4 tw-text-center">
          {hasMoreProducts && (
            <button
              onClick={onClickLoadMore}
              ref={loaderRef}
              className="btn btn-outline-darker btn-load-more tw-min-w-0 tw-py-3.5 tw-whitespace-nowrap tw-uppercase tw-text-base md:tw-text-lg lg:tw-text-xl lg:tw-py-4 tw-col-span-2 hover:tw-bg-[#1d1d1d] hover:tw-border-[#1d1d1d] focus:tw-bg-[#1d1d1d] focus:tw-border-[#1d1d1d] active:tw-bg-[#1d1d1d] active:tw-border-[#1d1d1d] tw-items-center tw-gap-1"
              disabled={isLoadingMoreProducts}
            >
              <span>More Good Stuff</span>{' '}
              {isLoadingMoreProducts ? (
                <i className="icon-refresh load-more-rotating" />
              ) : (
                <i className="icon-refresh" />
              )}
            </button>
          )}
        </div>
      </div>

      <ProductViewDialog
        product={selectedProduct}
        onClose={onCloseProductView}
        onFirstTimeAdded={triggerPostAddToCartPopup}
      />
    </>
  );
};

export default ProductsGrid;
